import { findContact } from './contacts'
import type { ActivityCategory, LogChannel, LoggedActivity } from '../types/activity'

interface SeedEntry {
  category: ActivityCategory
  count: number
  daysAgo: number
  timeLabel: string
  contactId: string
  channel?: LogChannel
  note?: string
  source?: 'manual' | 'prompt'
}

/**
 * Seeded activity history. Every entry is attributed to a realtor, prospect,
 * lead, or past client, and `daysAgo` drives the weekly/monthly/quarterly rollups.
 */
const seedEntries: SeedEntry[] = [
  // Today
  {
    category: 'realtor-outreach',
    count: 1,
    daysAgo: 0,
    timeLabel: '8:42 AM',
    contactId: 'RP-101',
    channel: 'call',
    note: 'Left voicemail, following up with a text this afternoon.',
  },
  {
    category: 'realtor-outreach',
    count: 1,
    daysAgo: 0,
    timeLabel: '8:55 AM',
    contactId: 'RP-102',
    channel: 'text',
  },
  {
    category: 'social-engagement',
    count: 2,
    daysAgo: 0,
    timeLabel: '8:15 AM',
    contactId: 'RP-103',
    channel: 'social',
    note: 'Commented on her two new listing posts.',
  },
  {
    category: 'social-engagement',
    count: 1,
    daysAgo: 0,
    timeLabel: '8:20 AM',
    contactId: 'A-2151',
    channel: 'social',
  },
  {
    category: 'consumer-conversation',
    count: 1,
    daysAgo: 0,
    timeLabel: '9:20 AM',
    contactId: 'L-40176',
    channel: 'call',
    note: 'Walked through rate-buydown options.',
  },
  // Yesterday
  {
    category: 'realtor-outreach',
    count: 2,
    daysAgo: 1,
    timeLabel: '4:05 PM',
    contactId: 'A-2201',
    channel: 'text',
  },
  {
    category: 'realtor-outreach',
    count: 2,
    daysAgo: 1,
    timeLabel: '4:20 PM',
    contactId: 'RP-106',
    channel: 'call',
  },
  {
    category: 'past-client-call',
    count: 1,
    daysAgo: 1,
    timeLabel: '2:30 PM',
    contactId: 'PC-201',
    channel: 'call',
    note: 'Quarterly check-in, escrow analysis scheduled.',
    source: 'prompt',
  },
  {
    category: 'past-client-call',
    count: 1,
    daysAgo: 1,
    timeLabel: '3:10 PM',
    contactId: 'PC-204',
    channel: 'call',
  },
  // This week
  {
    category: 'partner-meeting',
    count: 1,
    daysAgo: 2,
    timeLabel: '11:00 AM',
    contactId: 'A-2176',
    channel: 'in-person',
    note: 'Reviewed her spring listing plan, agreed to co-host an open house.',
  },
  {
    category: 'social-engagement',
    count: 3,
    daysAgo: 2,
    timeLabel: '7:50 AM',
    contactId: 'RP-105',
    channel: 'social',
  },
  {
    category: 'social-engagement',
    count: 3,
    daysAgo: 2,
    timeLabel: '8:05 AM',
    contactId: 'A-2170',
    channel: 'social',
  },
  {
    category: 'realtor-outreach',
    count: 3,
    daysAgo: 3,
    timeLabel: '10:10 AM',
    contactId: 'RP-104',
    channel: 'call',
  },
  {
    category: 'realtor-outreach',
    count: 3,
    daysAgo: 3,
    timeLabel: '10:40 AM',
    contactId: 'A-2165',
    channel: 'call',
  },
  {
    category: 'consumer-conversation',
    count: 2,
    daysAgo: 3,
    timeLabel: '1:15 PM',
    contactId: 'L-40188',
    channel: 'call',
  },
  {
    category: 'consumer-conversation',
    count: 1,
    daysAgo: 3,
    timeLabel: '2:40 PM',
    contactId: 'L-40154',
    channel: 'video',
  },
  {
    category: 'partner-meeting',
    count: 1,
    daysAgo: 5,
    timeLabel: '12:00 PM',
    contactId: 'RP-105',
    channel: 'in-person',
    note: 'Lunch — walked through her buyer pipeline for the fall.',
  },
  // Earlier this month
  {
    category: 'realtor-outreach',
    count: 5,
    daysAgo: 8,
    timeLabel: '9:00 AM',
    contactId: 'RP-107',
    channel: 'call',
  },
  {
    category: 'realtor-outreach',
    count: 4,
    daysAgo: 8,
    timeLabel: '9:45 AM',
    contactId: 'A-2188',
    channel: 'call',
  },
  {
    category: 'social-engagement',
    count: 6,
    daysAgo: 9,
    timeLabel: '8:00 AM',
    contactId: 'RP-102',
    channel: 'social',
  },
  {
    category: 'social-engagement',
    count: 5,
    daysAgo: 9,
    timeLabel: '8:30 AM',
    contactId: 'A-2140',
    channel: 'social',
  },
  {
    category: 'consumer-conversation',
    count: 4,
    daysAgo: 10,
    timeLabel: '3:00 PM',
    contactId: 'L-40131',
    channel: 'call',
  },
  {
    category: 'consumer-conversation',
    count: 3,
    daysAgo: 10,
    timeLabel: '4:00 PM',
    contactId: 'L-40208',
    channel: 'call',
  },
  {
    category: 'past-client-call',
    count: 2,
    daysAgo: 11,
    timeLabel: '10:45 AM',
    contactId: 'PC-206',
    channel: 'call',
    source: 'prompt',
  },
  {
    category: 'past-client-call',
    count: 2,
    daysAgo: 11,
    timeLabel: '11:30 AM',
    contactId: 'PC-203',
    channel: 'call',
    source: 'prompt',
  },
  {
    category: 'partner-meeting',
    count: 1,
    daysAgo: 12,
    timeLabel: '11:30 AM',
    contactId: 'A-2201',
    channel: 'in-person',
  },
  {
    category: 'partner-meeting',
    count: 1,
    daysAgo: 12,
    timeLabel: '2:00 PM',
    contactId: 'P-3310',
    channel: 'in-person',
  },
  {
    category: 'realtor-outreach',
    count: 9,
    daysAgo: 16,
    timeLabel: '9:30 AM',
    contactId: 'RP-103',
    channel: 'call',
  },
  {
    category: 'realtor-outreach',
    count: 8,
    daysAgo: 16,
    timeLabel: '10:30 AM',
    contactId: 'A-2151',
    channel: 'text',
  },
  {
    category: 'social-engagement',
    count: 8,
    daysAgo: 17,
    timeLabel: '8:10 AM',
    contactId: 'RP-101',
    channel: 'social',
  },
  {
    category: 'social-engagement',
    count: 6,
    daysAgo: 17,
    timeLabel: '8:40 AM',
    contactId: 'A-2176',
    channel: 'social',
  },
  {
    category: 'consumer-conversation',
    count: 5,
    daysAgo: 18,
    timeLabel: '2:00 PM',
    contactId: 'L-40214',
    channel: 'call',
  },
  {
    category: 'consumer-conversation',
    count: 4,
    daysAgo: 18,
    timeLabel: '3:30 PM',
    contactId: 'L-40219',
    channel: 'call',
  },
  {
    category: 'past-client-call',
    count: 3,
    daysAgo: 19,
    timeLabel: '1:00 PM',
    contactId: 'PC-202',
    channel: 'call',
    source: 'prompt',
  },
  {
    category: 'partner-meeting',
    count: 2,
    daysAgo: 20,
    timeLabel: '9:00 AM',
    contactId: 'A-2165',
    channel: 'in-person',
  },
  {
    category: 'partner-meeting',
    count: 1,
    daysAgo: 21,
    timeLabel: '1:00 PM',
    contactId: 'A-2176',
    channel: 'in-person',
  },
  // Earlier this quarter
  {
    category: 'realtor-outreach',
    count: 12,
    daysAgo: 30,
    timeLabel: '9:00 AM',
    contactId: 'RP-106',
    channel: 'call',
  },
  {
    category: 'realtor-outreach',
    count: 10,
    daysAgo: 30,
    timeLabel: '10:00 AM',
    contactId: 'A-2170',
    channel: 'call',
  },
  {
    category: 'social-engagement',
    count: 11,
    daysAgo: 34,
    timeLabel: '8:00 AM',
    contactId: 'RP-107',
    channel: 'social',
  },
  {
    category: 'social-engagement',
    count: 9,
    daysAgo: 34,
    timeLabel: '8:30 AM',
    contactId: 'A-2188',
    channel: 'social',
  },
  {
    category: 'consumer-conversation',
    count: 9,
    daysAgo: 38,
    timeLabel: '11:00 AM',
    contactId: 'L-40176',
    channel: 'call',
  },
  {
    category: 'consumer-conversation',
    count: 7,
    daysAgo: 38,
    timeLabel: '1:00 PM',
    contactId: 'L-40117',
    channel: 'call',
  },
  {
    category: 'past-client-call',
    count: 5,
    daysAgo: 42,
    timeLabel: '10:00 AM',
    contactId: 'PC-205',
    channel: 'call',
    source: 'prompt',
  },
  {
    category: 'past-client-call',
    count: 4,
    daysAgo: 42,
    timeLabel: '11:00 AM',
    contactId: 'PC-201',
    channel: 'call',
    source: 'prompt',
  },
  {
    category: 'partner-meeting',
    count: 3,
    daysAgo: 46,
    timeLabel: '11:00 AM',
    contactId: 'A-2140',
    channel: 'in-person',
  },
  {
    category: 'partner-meeting',
    count: 2,
    daysAgo: 46,
    timeLabel: '2:30 PM',
    contactId: 'P-3325',
    channel: 'in-person',
  },
  {
    category: 'realtor-outreach',
    count: 18,
    daysAgo: 58,
    timeLabel: '9:00 AM',
    contactId: 'RP-104',
    channel: 'call',
  },
  {
    category: 'realtor-outreach',
    count: 16,
    daysAgo: 58,
    timeLabel: '10:00 AM',
    contactId: 'A-2201',
    channel: 'call',
  },
  {
    category: 'social-engagement',
    count: 14,
    daysAgo: 63,
    timeLabel: '8:00 AM',
    contactId: 'RP-105',
    channel: 'social',
  },
  {
    category: 'social-engagement',
    count: 12,
    daysAgo: 63,
    timeLabel: '8:45 AM',
    contactId: 'A-2165',
    channel: 'social',
  },
  {
    category: 'consumer-conversation',
    count: 11,
    daysAgo: 70,
    timeLabel: '2:00 PM',
    contactId: 'L-40154',
    channel: 'call',
  },
  {
    category: 'consumer-conversation',
    count: 10,
    daysAgo: 70,
    timeLabel: '3:15 PM',
    contactId: 'L-40131',
    channel: 'call',
  },
  {
    category: 'past-client-call',
    count: 7,
    daysAgo: 78,
    timeLabel: '10:00 AM',
    contactId: 'PC-204',
    channel: 'call',
    source: 'prompt',
  },
  {
    category: 'past-client-call',
    count: 5,
    daysAgo: 78,
    timeLabel: '11:15 AM',
    contactId: 'PC-206',
    channel: 'call',
    source: 'prompt',
  },
  {
    category: 'partner-meeting',
    count: 4,
    daysAgo: 84,
    timeLabel: '11:00 AM',
    contactId: 'A-2176',
    channel: 'in-person',
  },
  {
    category: 'partner-meeting',
    count: 2,
    daysAgo: 88,
    timeLabel: '1:00 PM',
    contactId: 'RP-102',
    channel: 'in-person',
  },
]

export const activityLogSeed: LoggedActivity[] = seedEntries.map(
  ({ contactId, source, ...entry }, index) => ({
    id: `log-seed-${index + 1}`,
    ...entry,
    contact: findContact(contactId),
    source: source ?? 'manual',
  }),
)
